/**
 * Calc v3 §13.1 — Recalibration API wrapper.
 *
 * Surfaces the tenant's open catalog drift prompts and the operator actions
 * that resolve them. All endpoints 403 on tenants without
 * calculator_v3_enabled; the UI uses that signal to silently hide.
 */
import axiosInstance from './axios';

export type RecalibrationPromptStatus =
  | 'open'
  | 'dismissed'
  | 'applied'
  | 'special_cased';

export interface RecalibrationTaskRef {
  id: string;
  measure?: { id: string; name: string };
  measurePerHour?: number;
  name: string;
}

export interface RecalibrationPromptDto {
  catalogRate: number;
  createdAt: string;
  driftPercent: number;
  id: string;
  observedRate: number;
  sampleSize: number;
  status: RecalibrationPromptStatus;
  task: RecalibrationTaskRef;
  windowDays: number;
}

export interface ScanSummaryDto {
  companyId: string;
  driftThresholdPercent: number;
  minSampleSize: number;
  observations: unknown[];
  promptsOpened: number;
  promptsSkippedForCooldown: number;
  promptsUpdated: number;
  windowDays: number;
}

const listPending = async (): Promise<RecalibrationPromptDto[]> => {
  const { data } = await axiosInstance.get<RecalibrationPromptDto[]>(
    '/recalibration/pending',
  );
  return data;
};

const scan = async (): Promise<ScanSummaryDto> => {
  const { data } = await axiosInstance.post<ScanSummaryDto>(
    '/recalibration/scan',
    {},
  );
  return data;
};

const apply = async (promptId: string): Promise<RecalibrationPromptDto> => {
  const { data } = await axiosInstance.post<RecalibrationPromptDto>(
    `/recalibration/${promptId}/apply`,
  );
  return data;
};

const dismiss = async (
  promptId: string,
  cooldownDays?: number,
): Promise<RecalibrationPromptDto> => {
  const { data } = await axiosInstance.post<RecalibrationPromptDto>(
    `/recalibration/${promptId}/dismiss`,
    cooldownDays ? { cooldownDays } : {},
  );
  return data;
};

const flagSpecialCase = async (
  promptId: string,
): Promise<RecalibrationPromptDto> => {
  const { data } = await axiosInstance.post<RecalibrationPromptDto>(
    `/recalibration/${promptId}/flag-special-case`,
  );
  return data;
};

const recalibrationApi = {
  apply,
  dismiss,
  flagSpecialCase,
  listPending,
  scan,
};

export default recalibrationApi;
